import { onMounted, onBeforeUnmount, type Ref } from 'vue';
import { useFormBuilderStore } from '../model/form-builder.store';
import { useTableEditorStore } from '../model/table-editor.store';
import type { FormElement } from '../model/form.types';
import {
  removeFormBuilderBodyTable,
  removeFormBuilderCanvasElement,
  removeFormBuilderTableCellElement,
} from './removeFormBuilderCanvasElement';
import {
  isKeyboardEventFromEditableTarget,
  isKeyboardEventFromEnabledEditableTarget,
} from '../lib/isKeyboardEventFromEditableTarget';

function handleEscapeKey(tableEditorStore: ReturnType<typeof useTableEditorStore>) {
  if (tableEditorStore.selectedCells.length > 0) {
    tableEditorStore.clearCellSelection();
  } else {
    tableEditorStore.exitEditingMode();
  }
}

function getHistoryShortcut(e: KeyboardEvent): 'undo' | 'redo' | null {
  const normalizedKey = e.key.toLowerCase();
  const isModifierPressed = e.ctrlKey || e.metaKey;

  if (isModifierPressed && !e.shiftKey && normalizedKey === 'z') {
    return 'undo';
  }

  if (isModifierPressed && (normalizedKey === 'y' || (e.shiftKey && normalizedKey === 'z'))) {
    return 'redo';
  }

  return null;
}

function isInsideCanvas(target: EventTarget | null) {
  return target instanceof HTMLElement && !!target.closest('.canvas-background');
}

function handleHistoryShortcut(
  e: KeyboardEvent,
  formBuilderStore: ReturnType<typeof useFormBuilderStore>,
  shouldHandleHistoryShortcut: boolean
) {
  const historyShortcut = getHistoryShortcut(e);

  if (!shouldHandleHistoryShortcut || !historyShortcut || isKeyboardEventFromEditableTarget(e)) {
    return false;
  }

  e.preventDefault();

  if (historyShortcut === 'undo') {
    formBuilderStore.undo();
  } else {
    formBuilderStore.redo();
  }

  return true;
}

function handleEscapeShortcut(
  e: KeyboardEvent,
  formBuilderStore: ReturnType<typeof useFormBuilderStore>,
  tableEditorStore: ReturnType<typeof useTableEditorStore>
) {
  if (e.key !== 'Escape') return false;

  e.preventDefault();

  if (tableEditorStore.isEditingMode) {
    handleEscapeKey(tableEditorStore);
  } else {
    formBuilderStore.selectElement(null);
  }

  return true;
}

export interface CanvasKeyboardOptions {
  onRequestDelete?: (elementId: string) => void;
  onRequestDeleteCellElement?: (element: FormElement) => void;
  shouldHandleHistoryShortcut?: () => boolean;
}

export function useCanvasKeyboard(
  selectedElementId: Ref<string | null>,
  options: CanvasKeyboardOptions = {}
) {
  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();

  function handleDeleteCanvasElement() {
    if (!selectedElementId.value) return;

    if (options.onRequestDelete) {
      options.onRequestDelete(selectedElementId.value);
      return;
    }

    if (removeFormBuilderBodyTable(selectedElementId.value)) return;
    removeFormBuilderCanvasElement(selectedElementId.value);
  }

  function handleDeleteShortcut(e: KeyboardEvent) {
    if (e.key !== 'Delete' || !isInsideCanvas(e.target)) return false;
    if (isKeyboardEventFromEnabledEditableTarget(e)) return false;

    e.preventDefault();
    const cellContent = tableEditorStore.selectedCellData?.content;
    if (cellContent && options.onRequestDeleteCellElement) {
      options.onRequestDeleteCellElement(cellContent);
      return true;
    }

    if (removeFormBuilderTableCellElement()) {
      return true;
    }

    handleDeleteCanvasElement();
    return true;
  }

  const handleKeyboard = (e: KeyboardEvent) => {
    if (
      handleHistoryShortcut(
        e,
        formBuilderStore,
        options.shouldHandleHistoryShortcut?.() ?? true
      )
    ) return;
    if (handleEscapeShortcut(e, formBuilderStore, tableEditorStore)) return;
    handleDeleteShortcut(e);
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeyboard);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyboard);
  });

  return {
    handleKeyboard,
  };
}
